<template>
    <LandingPageLayout
        v-bind="landingPageSharedData.getLandingPageLayoutProps()"
    >
        <FeaturePageHeroSection
            title="Integrations that keep your client flow connected"
            subtitle="Sync Google Calendar, Outlook Calendar, conferencing, and payments so bookings feel professional from first click to confirmed session."
            buttonLabel="Connect your tools"
            :buttonUrl="heroRegisterUrl"
        />

        <section class="border-b border-slate-200 bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div class="grid gap-10 lg:grid-cols-2 lg:items-start">
                    <div>
                        <FeaturePageSectionHeader
                            title="Live integrations in your booking flow"
                            subtitle="Connect production-ready providers for Google Calendar, Outlook Calendar, meeting links, and payment collection."
                            subtitleClass="mt-3 text-slate-600"
                        />

                        <FeaturePagePrimaryButton
                            class="mt-7"
                            label="Connect your first integration"
                            :href="ctaRegisterUrl"
                        />
                    </div>

                    <div>
                        <FeaturePageMockPanel title="Integration Hub">
                            <div class="grid gap-3 sm:grid-cols-2">
                                <article
                                    v-for="integrationProvider in integrationHubProviders"
                                    :key="integrationProvider.title"
                                    class="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                >
                                    <div
                                        class="flex items-center justify-between gap-3"
                                    >
                                        <Image
                                            :src="integrationProvider.iconPath"
                                            :alt="`${integrationProvider.title} logo`"
                                            class="h-4 w-4 object-contain"
                                        />

                                        <FeaturePageBadge
                                            v-if="
                                                integrationProvider.isConnected
                                            "
                                            text="Connected"
                                            variant="success"
                                            size="xs"
                                        />
                                    </div>

                                    <h3
                                        class="mt-2 text-sm font-bold text-slate-900"
                                    >
                                        {{ integrationProvider.title }}
                                    </h3>
                                    <p class="mt-1 text-sm text-slate-600">
                                        {{ integrationProvider.description }}
                                    </p>
                                </article>
                            </div>
                        </FeaturePageMockPanel>
                    </div>
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Integration catalog"
                    subtitle="Core providers are available now, with planned additions ready to expand the tools solo experts rely on most."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="integrationFeature in integrationFeatures"
                        :key="integrationFeature.title"
                        :title="integrationFeature.title"
                        :description="integrationFeature.description"
                        containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                        :badgeText="
                            integrationFeature.isPlanned ? 'Planned' : null
                        "
                        badgeVariant="upcoming"
                        badgeSize="xs"
                        badgePosition="bottom"
                        badgeClass="mt-2"
                    />
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div class="text-center">
                    <FeaturePageBadge
                        text="Planned tracking integrations"
                        variant="upcoming"
                        uppercase
                    />
                </div>

                <FeaturePageSectionHeader
                    containerClass="mt-4 text-center"
                    title="Analytics with built-in consent awareness"
                    subtitle="Upcoming Google Analytics and Meta Pixel support is designed to work with a built-in cookie consent banner."
                    subtitleClass="mx-auto mt-3 max-w-3xl text-slate-600"
                />

                <div class="mt-8 grid gap-4 md:grid-cols-2">
                    <article
                        v-for="cookieConsentIntegration in cookieConsentIntegrations"
                        :key="cookieConsentIntegration.title"
                        class="rounded-xl border border-slate-200 bg-white p-5"
                    >
                        <h3 class="text-lg font-bold text-slate-900">
                            {{ cookieConsentIntegration.title }}
                        </h3>
                        <p class="mt-2 text-sm text-slate-600">
                            {{ cookieConsentIntegration.description }}
                        </p>
                    </article>
                </div>
            </div>
        </section>

        <section class="bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Sample use cases"
                    subtitle="Use integrations to move smoothly from booked to paid to ready-to-join without extra manual work."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="sampleUseCase in sampleUseCases"
                        :key="sampleUseCase.title"
                        :title="sampleUseCase.title"
                        :description="sampleUseCase.description"
                        containerClass="rounded-xl border border-slate-200 bg-slate-50 p-5"
                        titleClass="text-lg font-bold"
                        descriptionClass="mt-2 text-sm text-slate-600"
                    />
                </div>
            </div>
        </section>
    </LandingPageLayout>
</template>

<script setup lang="ts">
    import { UtmContentEnum } from '@/Shared/Service/Tracking/Utm/UtmEnums'
    import FeaturePageBadge from '@/Marketing/LandingPage/Component/FeaturePage/Badge.vue'
    import FeaturePageHeroSection from '@/Marketing/LandingPage/Component/FeaturePage/HeroSection.vue'
    import FeaturePageInfoCard from '@/Marketing/LandingPage/Component/FeaturePage/InfoCard.vue'
    import FeaturePageMockPanel from '@/Marketing/LandingPage/Component/FeaturePage/MockPanel.vue'
    import FeaturePagePrimaryButton from '@/Marketing/LandingPage/Component/FeaturePage/PrimaryButton.vue'
    import FeaturePageSectionHeader from '@/Marketing/LandingPage/Component/FeaturePage/SectionHeader.vue'
    import LandingPageSharedData from '@/Marketing/LandingPage/Entity/LandingPageSharedData'
    import LandingPageLayout from '@/Marketing/LandingPage/Shared/LandingPageLayout.vue'
    import Image from '@/Shared/Component/Image.vue'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface LandingPageProps {
        landingPageSharedDataRawData: any
    }

    interface IntegrationFeature {
        title: string
        description: string
        isPlanned: boolean
    }

    interface IntegrationHubProvider {
        title: string
        description: string
        iconPath: string
        isConnected: boolean
    }

    interface CookieConsentIntegration {
        title: string
        description: string
    }

    interface SampleUseCase {
        title: string
        description: string
    }

    const props = defineProps<LandingPageProps>()

    const landingPageSharedData = LandingPageSharedData.hydrate(
        props.landingPageSharedDataRawData,
    )
    const urlFactory = new UrlFactory()
    const heroRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.IntegrationsHero,
    )
    const ctaRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.IntegrationsCta,
    )

    const integrationFeatures: IntegrationFeature[] = [
        {
            title: 'Google Calendar',
            description:
                'Keep availability and booked events synced with your calendar in real time.',
            isPlanned: false,
        },
        {
            title: 'Outlook Calendar',
            description:
                'Sync availability and booked sessions with Outlook Calendar so scheduling stays accurate across your workday.',
            isPlanned: false,
        },
        {
            title: 'Google Meet',
            description:
                'Create meeting links automatically as soon as bookings are confirmed.',
            isPlanned: false,
        },
        {
            title: 'Zoom',
            description:
                'Auto-generate Zoom meeting links so hosts and attendees get ready-to-join details.',
            isPlanned: false,
        },
        {
            title: 'Stripe',
            description:
                'Collect online payments directly during booking without external checkout steps.',
            isPlanned: false,
        },
        {
            title: 'PayPal',
            description:
                'Add PayPal as an additional payment option if your clients prefer it.',
            isPlanned: true,
        },
        {
            title: 'Google Analytics',
            description:
                'Measure conversion performance with built-in support for cookie consent controls.',
            isPlanned: true,
        },
        {
            title: 'Meta Pixel',
            description:
                'Track ad campaign outcomes while respecting built-in cookie consent settings.',
            isPlanned: true,
        },
    ]

    const integrationHubProviders: IntegrationHubProvider[] = [
        {
            title: 'Google Calendar',
            description: 'Availability and bookings are syncing in real time.',
            iconPath: '/static/icons/providers/google-calendar.svg',
            isConnected: true,
        },
        {
            title: 'Outlook Calendar',
            description:
                'Availability stays aligned with Outlook Calendar automatically.',
            iconPath: '/static/icons/providers/outlook-calendar.webp',
            isConnected: true,
        },
        {
            title: 'Google Meet',
            description:
                'Meeting links are auto-created when bookings are confirmed.',
            iconPath: '/static/icons/providers/google-meet.svg',
            isConnected: true,
        },
        {
            title: 'Zoom',
            description:
                'Video meeting links are generated automatically for sessions.',
            iconPath: '/static/icons/providers/zoom.svg',
            isConnected: true,
        },
        {
            title: 'Stripe',
            description:
                'Payment collection is active directly in booking flows.',
            iconPath: '/static/icons/providers/stripe.svg',
            isConnected: true,
        },
    ]

    const cookieConsentIntegrations: CookieConsentIntegration[] = [
        {
            title: 'Google Analytics',
            description:
                'Track booking conversion behavior while honoring user consent choices through built-in consent handling.',
        },
        {
            title: 'Meta Pixel',
            description:
                'Measure ad attribution with consent-first tracking controls so marketing data stays policy-aligned.',
        },
    ]

    const sampleUseCases: SampleUseCase[] = [
        {
            title: 'Consulting intake funnel',
            description:
                'Drive paid strategy calls from ads and track conversions from first click to booked meeting.',
        },
        {
            title: 'Coaching delivery',
            description:
                'Auto-create meeting links and calendar events so coaches avoid manual scheduling work.',
        },
        {
            title: 'Solo expert marketing visibility',
            description:
                'Connect payments and analytics to understand which channels generate booked sessions and paid clients.',
        },
    ]
</script>
